import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/landing-redesign.css';
import '../styles/events-redesign.css';
import eventsData from '../data/events.json';

interface Event {
  id: string;
  name: string;
  category: string;
  description: string;
}

const TrackEventsRedesign: React.FC = () => {
  const [trackEvents, setTrackEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isNavScrolled, setIsNavScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll handler for nav
    const handleScroll = () => {
      setIsNavScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Simulate loading and filter for track events
    const timer = setTimeout(() => {
      const filteredEvents = eventsData.filter(event => event.category === 'Track');
      setTrackEvents(filteredEvents);
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Running icon SVG
  const RunningIcon = () => (
    <svg className="events-type-icon" viewBox="0 0 24 24" fill="currentColor">
      <path d="M13.5,5.5C14.59,5.5 15.5,4.58 15.5,3.5C15.5,2.38 14.59,1.5 13.5,1.5C12.39,1.5 11.5,2.38 11.5,3.5C11.5,4.58 12.39,5.5 13.5,5.5M9.89,19.38L10.89,15L13,17V23H15V15.5L12.89,13.5L13.5,10.5C14.79,12 16.79,13 19,13V11C17.09,11 15.5,10 14.69,8.58L13.69,7C13.29,6.38 12.69,6 12,6C11.69,6 11.5,6.08 11.19,6.19L6,8.28V13H8V9.58L9.79,8.88L8.19,17L3.29,16L2.89,18L9.89,19.38Z"/>
    </svg>
  );

  return (
    <div className="events-redesign landing-redesign">
      {/* Animated Background Elements */}
      <div className="track-pattern-bg" />
      <div className="particle-field">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="particle" />
        ))}
      </div>

      {/* Track Lane Background */}
      <div className="events-track-lines">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="track-lane" />
        ))}
        {[...Array(5)].map((_, i) => (
          <div key={i} className="speed-line" />
        ))}
      </div>

      {/* Navigation */}
      <nav className={`athletic-nav ${isNavScrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <Link to="/" className="nav-brand">
            <img 
              src="/assets/moe-logo.jpg" 
              alt="MOE Athletics" 
              className="nav-logo-img"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Ccircle cx="50" cy="50" r="45" fill="%23C41E3A"/%3E%3Ctext x="50" y="60" text-anchor="middle" fill="white" font-size="24" font-weight="bold"%3EMOE%3C/text%3E%3C/svg%3E';
              }}
            />
            <div className="nav-brand-text">
              <h1 className="nav-title">Athletics</h1>
              <span className="nav-subtitle">Regional Inter Secondary Schools</span>
            </div>
          </Link>

          <div className="nav-menu">
            <Link to="/" className="nav-menu-link">Home</Link>
            <Link to="/track-events" className="nav-menu-link active">Track Events</Link>
            <Link to="/field-events" className="nav-menu-link">Field Events</Link>
          </div>

          <div className="nav-actions">
            <button 
              className="btn-athletic btn-secondary-athletic"
              onClick={() => navigate('/')}
            >
              Login
            </button>
            <button 
              className="btn-athletic btn-primary-athletic"
              onClick={() => navigate('/')}
            >
              Register
            </button>
          </div>

          <button className="mobile-menu-toggle">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="events-hero">
        <div 
          className="events-hero-bg" 
          style={{ backgroundImage: 'url(/assets/landingwallpaper.jpg)' }}
        />
        
        {/* Diagonal Stripes */}
        <div className="events-hero-stripes">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="hero-stripe" />
          ))}
        </div>
        
        <div className="events-hero-overlay" />

        <div className="events-hero-content">
          <div className="events-type-badge">
            <RunningIcon />
            <span className="events-type-text">Speed & Endurance</span>
          </div>

          <h1 className="events-page-title">
            <span className="title-highlight">Track</span> Events
          </h1>

          <p className="events-page-subtitle">
            Sprint, middle distance, long distance, and relay competitions
          </p>

          <div className="events-stats-row">
            <div className="event-stat-item">
              <div className="event-stat-number">{trackEvents.length}</div>
              <div className="event-stat-label">Events</div>
            </div>
            <div className="event-stat-item">
              <div className="event-stat-number">4</div>
              <div className="event-stat-label">Age Groups</div>
            </div>
            <div className="event-stat-item">
              <div className="event-stat-number">2</div>
              <div className="event-stat-label">Genders</div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Grid Section */}
      <section className="events-grid-section">
        <div className="events-grid-header">
          <h2 className="events-section-title">All Track Events</h2>
          <div className="events-count-badge">
            {trackEvents.length} Events Available
          </div>
        </div>

        {isLoading ? (
          <div className="events-loading-athletic">
            <div className="loading-track">
              <div className="loading-runner" />
            </div>
            <span className="loading-text">Loading Track Events...</span>
          </div>
        ) : trackEvents.length === 0 ? (
          <div className="events-empty-athletic">
            <div className="events-empty-icon">🏃</div>
            <h3 className="events-empty-title">No Track Events Found</h3>
            <p className="events-empty-text">Check back soon for upcoming track events.</p>
          </div>
        ) : (
          <div className="events-grid-athletic">
            {trackEvents.map((event, index) => (
              <article 
                key={event.id} 
                className="event-card-athletic"
                style={{ 
                  animationDelay: `${index * 0.1}s`,
                  animation: 'fadeInUp 0.6s ease forwards',
                  opacity: 0
                }}
              >
                <div className="event-card-header">
                  <h3 className="event-card-title">{event.name}</h3>
                  <span className="event-category-badge">{event.category}</span>
                </div>

                <p className="event-card-description">{event.description}</p>

                <div className="event-tags-container">
                  {/* Gender Tags */}
                  <div className="event-tags-group">
                    <span className="event-tags-label">Gender Categories</span>
                    <div className="event-tags-list">
                      <span className="event-tag male">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '4px' }}>
                          <path d="M9.5,11c1.93,0,3.5,1.57,3.5,3.5S11.43,18,9.5,18S6,16.43,6,14.5S7.57,11,9.5,11M9.5,9C6.46,9,4,11.46,4,14.5S6.46,20,9.5,20s5.5-2.46,5.5-5.5c0-1.16-0.36-2.23-0.97-3.12L18,7.42V10h2V4h-6v2h2.58l-3.97,3.97C11.73,9.36,10.66,9,9.5,9z"/>
                        </svg>
                        Male
                      </span>
                      <span className="event-tag female">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '4px' }}>
                          <path d="M12,4A6,6 0 0,1 18,10C18,12.97 15.84,15.44 13,15.92V18H15V20H13V22H11V20H9V18H11V15.92C8.16,15.44 6,12.97 6,10A6,6 0 0,1 12,4M12,6A4,4 0 0,0 8,10A4,4 0 0,0 12,14A4,4 0 0,0 16,10A4,4 0 0,0 12,6Z"/>
                        </svg>
                        Female
                      </span>
                    </div>
                  </div>

                  {/* Age Tags */}
                  <div className="event-tags-group">
                    <span className="event-tags-label">Age Categories</span>
                    <div className="event-tags-list">
                      <span className="event-tag age">Grade 7</span>
                      <span className="event-tag age">Under 15</span>
                      <span className="event-tag age">Under 17</span>
                      <span className="event-tag age">Under 20</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Add keyframe animation */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default TrackEventsRedesign;
